/**
 * SelectActionsDetroitPhase - Detroit Bankruptcy Solo アクション選択フェーズUIコンポーネント
 *
 * Detroit固有ルール:
 * - 3種類のアクション（ENGINEER, LOCOMOTIVE, URBANIZATION）
 * - 各アクション3回まで（ディスク制）
 * - 手数料: 3枚残り=$3, 2枚=$2, 1枚=$1
 *
 * @see FRONTEND.md Step.3 準拠: Lovableスタイル適用済み（Barbadosと統一）
 */

import type { DetroitGameState, DetroitActionType } from '../../types';
import { getDetroitActionFee } from '../../types';
import { useTranslations } from '../../i18n';

export interface SelectActionsDetroitPhaseProps {
  G: DetroitGameState;
  isMyTurn: boolean;
  onSelectAction: (action: DetroitActionType) => void;
  /** スキップ（パス）コールバック - Puerto Ricoと統一 */
  onSkip: () => void;
}

/**
 * Phase 3: アクション選択フェーズUI（Detroit専用）
 */
export function SelectActionsDetroitPhase({
  G,
  isMyTurn,
  onSelectAction,
  onSkip,
}: SelectActionsDetroitPhaseProps) {
  const t = useTranslations();
  const player = G.players['0'];

  // Detroitのアクション定義（i18n対応）
  const DETROIT_ACTIONS: {
    type: DetroitActionType;
    name: string;
    description: string;
  }[] = [
    { type: 'ENGINEER', name: t.selectActions.actions.engineer.name, description: t.selectActions.actions.engineer.description },
    { type: 'LOCOMOTIVE', name: t.selectActions.actions.locomotive.name, description: t.selectActions.actions.locomotive.description },
    { type: 'URBANIZATION', name: t.selectActions.actions.urbanization.name, description: t.selectActions.actions.urbanization.description },
  ];

  return (
    <div className="space-y-4">
      {!isMyTurn ? (
        <p className="text-muted-foreground text-sm">{t.common.waiting}</p>
      ) : (
        <>
          {/* アクション選択ボタン */}
          <div className="grid grid-cols-2 gap-1.5">
            {DETROIT_ACTIONS.map((action) => {
              const disksRemaining = G.actionDisks[action.type];
              const fee = getDetroitActionFee(disksRemaining);
              const isDisabled = disksRemaining === 0 || player.cash < fee;

              return (
                <button
                  key={action.type}
                  className="action-card text-left"
                  onClick={() => !isDisabled && onSelectAction(action.type)}
                  disabled={isDisabled}
                  data-action={action.type}
                  data-disabled={isDisabled}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-heading text-sm font-semibold text-foreground leading-tight">
                        {action.name}
                      </div>
                      <div className="text-[10px] text-muted-foreground leading-tight">
                        {action.description}
                      </div>
                    </div>
                    <div className="text-right">
                      {disksRemaining > 0 ? (
                        <span className="text-sm font-bold text-warning">${fee}</span>
                      ) : (
                        <span className="text-[10px] text-destructive">{t.selectActions.used}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {disksRemaining}/3 disks
                  </div>
                </button>
              );
            })}
          </div>

          {/* スキップボタン */}
          <button
            className="btn-secondary w-full py-2"
            onClick={onSkip}
          >
            {t.selectActions.detroit?.skip || 'Skip (No Cost)'}
          </button>

          {/* ヘルプテキスト */}
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer hover:text-foreground">{t.selectActions.helpTitle}</summary>
            <ul className="mt-2 ml-4 list-disc space-y-1">
              <li>{t.selectActions.detroit?.helpOptional || 'Action selection is optional'}</li>
              <li>{t.selectActions.detroit?.helpCost || '3 disks=$3, 2 disks=$2, 1 disk=$1'}</li>
              <li>{t.selectActions.detroit?.helpLocomotive || 'Each action can be used up to 3 times'}</li>
            </ul>
          </details>
        </>
      )}
    </div>
  );
}

export default SelectActionsDetroitPhase;
